import React, {
  ReactNode,
  createContext,
  useContext,
  useMemo,
} from 'react';

import { useGetDocumentacion } from '../hooks/Reintegro/Documentacion/useDocumentacion';
import { Documentacion } from '../types/Reintegro/types';
import { useSiniestros } from './SiniestrosContext';

type DocumentacionContextType = {
  documentacion: Documentacion | undefined;
  mutateDocumentacion: () => Promise<Documentacion | undefined>;
  isValidating: boolean;
};

const DocumentacionContext = createContext<DocumentacionContextType | undefined>(undefined);

interface ProviderProps {
  children: ReactNode;
}

/**
 * Context que maneja la documentación del siniestro actual.
 *
 * Este context solo cambia cuando:
 * - La documentación del siniestro actual se actualiza (fetch, mutate)
 * - El estado de validación cambia
 *
 * Consume currentSiniestro de SiniestrosContext.
 */
export const DocumentacionContextProvider: React.FC<ProviderProps> = ({ children }) => {
  const { currentSiniestro } = useSiniestros();

  const {
    documentacion,
    isValidating,
    mutate: mutateDocumentacion,
  } = useGetDocumentacion(currentSiniestro?.id);

  const contextValue = useMemo(
    () => ({
      documentacion,
      mutateDocumentacion,
      isValidating,
    }),
    [documentacion, mutateDocumentacion, isValidating],
  );

  return (
    <DocumentacionContext.Provider value={contextValue}>
      {children}
    </DocumentacionContext.Provider>
  );
};

export const useDocumentacion = (): DocumentacionContextType => {
  const context = useContext(DocumentacionContext);
  if (!context) {
    throw new Error('useDocumentacion must be used within a DocumentacionContextProvider');
  }
  return context;
};
